import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface User {
  id?: number;
  email: string;
  user_type: 'employee' | 'employer' | 'admin';
  status: 'active' | 'inactive' | 'suspended';
  email_verified: boolean;
  created_at?: string;
  updated_at?: string;
  profile?: {
    user_id: number;
    first_name?: string;
    last_name?: string;
    phone?: string;
    location?: string;
    bio?: string;
    profile_image?: string;
    company_name?: string;
    company_size?: string;
    linkedin_url?: string;
    website_url?: string;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// User API functions
export const userApi = {
  // Get all users
  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    user_type?: string;
  }): Promise<ApiResponse<User[]>> => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  // Get user by ID
  getUser: async (id: number): Promise<ApiResponse<User>> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Create user
  createUser: async (userData: {
    user: Partial<User>;
    profile?: Partial<User['profile']>;
  }): Promise<ApiResponse<{ id: number }>> => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  // Update user
  updateUser: async (
    id: number,
    userData: {
      user?: Partial<User>;
      profile?: Partial<User['profile']>;
    }
  ): Promise<ApiResponse<any>> => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  // Delete user
  deleteUser: async (id: number): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  // Get user statistics
  getUserStats: async (): Promise<ApiResponse<{ total: number }>> => {
    const response = await api.get('/users/stats');
    return response.data;
  },
};

export default api;
